import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from '../../../components/form/form.component';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { styled } from '@mui/material/styles';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { createProject, updateProject } from '../../../redux/thunks/admin/projects';
import { newProjectForm, newProjectForm_payments, newProjectForm_credits } from '../../../utils/form-configs/projects.form';
import { cleanFormData } from '../../../utils/helpers/form';

const ProjectModal = ({ show, onClose, project }) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { isCreating, isUpdating } = useSelector(state => state.entities.admin.projects);
  const {
    register,
    unregister,
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
    watch,
    clearErrors,
  } = useForm({
    defaultValues: project ? {
      name: project.name,
      status: project.status,
      users: project.users,
      payments: project.payments.map(({ paidAt, paidAmount, paidHours, hourlyRate }) => ({
        paidAt: new Date(paidAt).toISOString().split('T')[0],
        paidAmount,
        paidHours,
        hourlyRate,
      })),
      credits: project.credits.map(({ givenAt, creditAmount, creditHours, note }) => ({
        givenAt: new Date(givenAt).toISOString().split('T')[0],
        creditAmount,
        creditHours,
        note
      })),
    } : {},
  });

  const AntTab = styled((props) => <Tab disableRipple {...props} />)(({ theme }) => ({
    textTransform: 'none',
    minWidth: 0,
    [theme.breakpoints.up('sm')]: {
      minWidth: 0,
    },
    fontWeight: theme.typography.fontWeightRegular,
    marginRight: theme.spacing(1),
    color: 'rgba(0, 0, 0, 0.85)',
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    '&:hover': {
      color: '#40a9ff',
      opacity: 1,
    },
    '&.Mui-selected': {
      color: '#1890ff',
      fontWeight: theme.typography.fontWeightMedium,
    },
    '&.Mui-focusVisible': {
      backgroundColor: '#d1eaff',
    },
  }));

  const tabs = [
    {
      label: 'Payments',
      form: newProjectForm_payments
    },
    {
      label: 'Credits',
      form: newProjectForm_credits
    }
  ];
  const [selectedTab, setSelectedTab] = useState({
    index: 0,
    tab: tabs[0],
  });

  useEffect(() => {
    watch(() => clearErrors('invalid'));
  }, [watch, clearErrors]);

  const onSubmit = (formData) => {
    const onSuccess = (message) => {
      enqueueSnackbar(message, { variant: 'success' });
      onClose();
    };
    const onFailure = (message) => enqueueSnackbar(message, { variant: 'error' });

    formData = cleanFormData(formData, newProjectForm);
    
    if (!project) {
      dispatch(createProject({ formData, onSuccess, onFailure }));
    } else {
      dispatch(updateProject({ formData: {
        _id: project._id,
        ...formData,
        users: formData.users.map(user => user._id ? user._id : user),
      }, onSuccess, onFailure }));
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{project ? 'Edit' : 'Add'} Project</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form
          form={newProjectForm}
          useForm={{
            register,
            unregister,
            errors,
            setValue,
            getValues,
          }}
        />
        
        <Tabs
         value={selectedTab.index}
         onChange={(event, index) => setSelectedTab({
          index,
          tab: tabs[index],
         })}
         variant="scrollable"
         scrollButtons={false}
         aria-label="modal-tabs"
        >{tabs.map((tab, index) => <AntTab key={index} label={tab.label} />)}</Tabs>
        <Form
          form={selectedTab.tab.form}
          useForm={{
            register,
            unregister,
            errors,
            setValue,
            getValues,
          }}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSubmit(onSubmit)} disabled={!project ? isCreating : isUpdating[project._id]}>
          {project ? 'Edit' : 'Add'} Project
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProjectModal;
